import axios from 'axios'

const messages = {
  401: '登录已失效，请重新登录',
  403: '没有访问权限',
  404: '请求的资源不存在',
  409: '配置已发生变化，请重新加载后再保存',
  422: '请求参数或配置不正确',
  429: '请求过于频繁，请稍后重试'
}

export function readStoredToken () {
  const token = localStorage.getItem('x-access-token')
  return token && token !== 'undefined' && token !== 'null' ? token : ''
}

export function normalizeRequestError (error) {
  if (axios.isCancel(error)) return error
  const response = error.response || {}
  const status = response.status || 0
  const data = response.data || {}
  const timeout = error.code === 'ECONNABORTED'
  const normalized = new Error(data.message || messages[status] ||
    (timeout ? '请求超时，请稍后重试' : status ? '请求失败，请稍后重试' : '网络连接异常，请检查连接'))
  // 兼容旧页面使用 error.status / error.data 的方式，也保留 Axios 响应。
  normalized.status = status
  normalized.data = data
  normalized.response = error.response
  normalized.code = timeout ? 'TIMEOUT' : status ? 'HTTP_ERROR' : 'NETWORK_ERROR'
  return normalized
}

let auth = { getToken: readStoredToken, onUnauthorized: () => {} }

export function configureHttpAuth (options) {
  auth = Object.assign({}, auth, options)
}

export function createHttpClient (options = {}) {
  const client = axios.create({ timeout: 30000, ...options.defaults })
  client.interceptors.request.use(config => {
    const token = (options.getToken || auth.getToken)()
    if (token) config.headers['X-Access-Token'] = token
    return config
  })
  client.interceptors.response.use(response => response, error => {
    const normalized = normalizeRequestError(error)
    if (normalized.status === 401) (options.onUnauthorized || auth.onUnauthorized)(normalized)
    return Promise.reject(normalized)
  })
  return client
}

export const createRequestCancellation = () => axios.CancelToken.source()
export const isRequestCanceled = error => axios.isCancel(error)

// 旧Mock.js的XHR包装不透传responseType；二进制读取使用原生fetch，沿用统一鉴权和错误处理。
export async function requestBinary ({ url, params = {}, timeout = 10000 }) {
  if (typeof url !== 'string' || !/^\/(?!\/)/.test(url) || /[\\\r\n]/.test(url)) throw new Error('文件地址必须使用当前平台接口')
  const query = new URLSearchParams(params).toString()
  const token = auth.getToken(), headers = {}
  if (token) headers['X-Access-Token'] = token
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), timeout)
  try {
    const response = await fetch(url + (query ? (url.includes('?') ? '&' : '?') + query : ''),
      { method: 'GET', headers, credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal })
    if (!response.ok) {
      let data = {}
      try { data = await response.json() } catch (_) { /* 非JSON错误沿用HTTP状态提示。 */ }
      throw { response: { status: response.status, data } }
    }
    return { status: response.status, data: await response.arrayBuffer() }
  } catch (error) {
    const normalized = normalizeRequestError(error.name === 'AbortError' ? { code: 'ECONNABORTED' } : error)
    if (normalized.status === 401) auth.onUnauthorized(normalized)
    throw normalized
  } finally { clearTimeout(timer) }
}

// 隐藏/关闭页面时直接发送配置，保留平台令牌头；sendBeacon 无法携带该鉴权头。
// 浏览器的 keepalive 队列约为64KiB，大配置在隐藏时仍正常发送，并保留离页提醒。
export async function requestKeepalive ({ url, data }) {
  const body = JSON.stringify(data)
  const token = auth.getToken()
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['X-Access-Token'] = token
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 30000)
  try {
    const response = await fetch(url, { method: 'POST', headers, body, credentials: 'same-origin',
      keepalive: new Blob([body]).size <= 60 * 1024, signal: controller.signal })
    const result = { status: response.status, data: await response.json() }
    if (!response.ok) throw { response: result }
    return result
  } catch (error) {
    const normalized = normalizeRequestError(error.name === 'AbortError' ? { code: 'ECONNABORTED' } : error)
    if (normalized.status === 401) auth.onUnauthorized(normalized)
    throw normalized
  } finally { clearTimeout(timer) }
}
const http = createHttpClient()
// 仅原生只读 API 使用；分享页可提供绑定访问者身份的独立传输，不改全局登录态。
export function platformRead (config, options = {}) {
  const { readClient, ...requestOptions } = options
  return (readClient || http)({ ...requestOptions, ...config })
}
export default http
